/**
 * A day of the week, as one vocabulary (D-105).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  WHY THIS EXISTS RATHER THAN A SECOND NUMBERING
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  `WeekStartDay` has meant "0 = Sunday … 6 = Saturday, matching
 *  `Date.prototype.getDay`" since D-034, and a scheduled session needs exactly
 *  the same thing. **Two numberings for one concept is how a schedule ends up a
 *  day out from the week it is materialised into**, and the failure would be
 *  invisible for six days out of seven.
 *
 *  So `WeekStartDay` becomes an alias of this type rather than a sibling of it.
 *  The name survives because it says which question is being asked — *which day
 *  does a week begin on* — while `Weekday` says only *which day*.
 *
 *  Nothing here is a setting and nothing here is stored on its own. The labels
 *  are presentation, exactly as `FITNESS_DOMAIN_LABELS` is: never persisted,
 *  never compared.
 */

export const WEEKDAYS = [0, 1, 2, 3, 4, 5, 6] as const

export type Weekday = (typeof WEEKDAYS)[number]

export const WEEKDAY_LABELS: Readonly<Record<Weekday, string>> = {
  0: 'Sunday',
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
}

export function isWeekday(value: number): value is Weekday {
  return Number.isInteger(value) && value >= 0 && value <= 6
}

/**
 * Where a weekday falls in a week that begins on `weekStartDay`.
 *
 * `0` is the first day of that week and `6` the last. This is the whole of what
 * "chronological within the week" means once the week's start is configurable,
 * and it is a pure function of two numbers so that no caller has to reason about
 * it twice.
 *
 * **It answers a position, never a date.** Turning a position into a calendar
 * date needs the week's start date, which belongs to the materialised week
 * (D-105 §6) rather than to the schedule.
 */
export function weekdayOffset(weekday: Weekday, weekStartDay: Weekday): number {
  return (weekday - weekStartDay + 7) % 7
}

/**
 * The seven weekdays in the order a week beginning on `weekStartDay` runs.
 *
 * Used by anything that lays a week out — the schedule editor, Today's weekly
 * context, the materialised week. **No component hard-codes Monday-first**
 * (D-106), and this is the function that makes that cheap to obey.
 */
export function weekdaysFrom(weekStartDay: Weekday): readonly Weekday[] {
  return WEEKDAYS.map((_, index) => ((weekStartDay + index) % 7) as Weekday)
}
